import { DataType, DecimalScale, SelectOption, DataTableColumn } from '@globaleris/ui.library'
import i18next from 'i18next'
import { EnumEstadoViajeOpLogistica } from '../../../../api/oData/enums/enumEstadoViajeOpLogistica'
import { enumToSelectOptionArray } from '../../../../helpers'
import { DateFilterChoice } from '../../../../components'
import { DateFilters, UserRoles } from '../../../../model'
import { hasRole } from '../../../../helpers/permissionsHelper'
import { usePageState } from '../../../../hooks'

interface TanksFilters {
  dateFilters: DateFilterChoice[]
  customFilter: boolean
  genericFilters: GenericFilter[]
}

export type GenericFilter = SelectOption & {
  id: number
  initiallySelected?: boolean
  // configuration?: GenericFilterConfiguration
  enabled?: boolean
  inactive?: boolean
}

export const getFilters = (dateFilter: any, customFilter: any): TanksFilters => {
  let filters: TanksFilters = {
    dateFilters: [],
    customFilter: false,
    genericFilters: []
  }
  const userRoleGest =
    hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) || hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO)
  const userRoleTPA = hasRole(UserRoles.TANKS_REPSOL_TPA)

  // Todos los roles ven CustomFilter
  filters.customFilter = true

  if (!(userRoleGest || userRoleTPA)) {
    // Roles standard
    filters.dateFilters.push({ filter: DateFilters.TODAY }, { filter: DateFilters.TOMORROW_ONLY })
    filters.dateFilters.push(
      {
        filter: DateFilters.WEEK
      },
      { filter: DateFilters.THIRTYDAYS }
    )
  } else if (userRoleTPA) {
    // Roles TPA de Repsol
    filters.dateFilters.push({ filter: DateFilters.TODAY }, { filter: DateFilters.TOMORROW_ONLY })
    filters.genericFilters.push(
      {
        id: 1,
        value: EnumEstadoViajeOpLogistica.Registrado,
        label: i18next.t('enums:EnumEstadoViajeOpLogistica.Registrado'),
        initiallySelected: true,
        enabled: true
      },
      {
        id: 2,
        value: EnumEstadoViajeOpLogistica.Validado,
        label: i18next.t('enums:EnumEstadoViajeOpLogistica.Validado'),
        enabled: true
      }
    )
  } else if (userRoleGest) {
    // Roles Gest y NoGest de Repsol
    filters.genericFilters.push({
      id: 3,
      value: 'lastHundred',
      label: 'Últimos 100',
      initiallySelected: !(customFilter || dateFilter),
      enabled: true
    })
  }

  return filters
}

export const getTanksListColumns = ({
  carriers,
  destinations,
  originPlants
}: {
  carriers: SelectOption[]
  destinations: SelectOption[]
  originPlants: SelectOption[]
}): DataTableColumn[] => {
  const managedRole = hasRole(UserRoles.TANKS_REPSOL_GESTIONADO)
  const notManagedRole = hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO)
  const TPARole = hasRole(UserRoles.TANKS_REPSOL_TPA)
  if (managedRole) {
    return [
      {
        name: 'actions',
        label: '',
        type: DataType.HTML,
        columnWidth: 25
      },
      {
        name: 'id',
        display: false
      },
      {
        name: 'orderDate',
        label: i18next.t('tanks:dataTable.columns.orderDate'),
        type: DataType.DATE
      },
      {
        name: 'tripDate',
        label: i18next.t('tanks:dataTable.columns.loadDate'),
        type: DataType.DATE
      },
      {
        name: 'dischargeDate',
        label: i18next.t('tanks:dataTable.columns.dischargeDate'),
        type: DataType.DATE
      },
      {
        name: 'tsoCode',
        label: i18next.t('tanks:dataTable.columns.orderNumber'),
        type: DataType.TEXT
      },
      {
        name: 'client.description',
        label: i18next.t('tanks:dataTable.columns.client'),
        type: DataType.TEXT
      },
      {
        name: 'destinations',
        label: i18next.t('tanks:dataTable.columns.destination'),
        type: DataType.MULTISELECT,
        selectOptions: destinations
      },
      {
        name: 'orderKg',
        label: i18next.t('tanks:dataTable.columns.orderKg'),
        type: DataType.NUMBER,
        decimalScale: DecimalScale.DEFAULT
      },
      {
        name: 'carriers',
        label: i18next.t('tanks:dataTable.columns.carrier'),
        type: DataType.MULTISELECT,
        selectOptions: carriers
      },
      {
        name: 'checkCompleted',
        label: i18next.t('tanks:dataTable.columns.completed'),
        type: DataType.BOOLEAN
      },
      {
        name: 'managedTripState',
        label: i18next.t('tanks:dataTable.columns.travelStatus'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(
          EnumEstadoViajeOpLogistica,
          'enums:EnumEstadoViajeOpLogistica'
        )
      },
      {
        name: 'relatedOperations',
        display: false
      }
    ]
  } else if (notManagedRole) {
    return [
      {
        name: 'actions',
        label: '',
        type: DataType.HTML,
        columnWidth: 25
      },
      {
        name: 'id',
        display: false
      },
      {
        name: 'orderDate',
        label: i18next.t('tanks:dataTable.columns.orderDate'),
        type: DataType.DATE
      },
      {
        name: 'tripDate',
        label: i18next.t('tanks:dataTable.columns.loadDate'),
        type: DataType.DATE
      },
      {
        name: 'dischargeDate',
        label: i18next.t('tanks:dataTable.columns.dischargeDate'),
        type: DataType.DATE
      },
      {
        name: 'tsoCode',
        label: i18next.t('tanks:dataTable.columns.orderNumber'),
        type: DataType.TEXT
      },
      {
        name: 'client.description',
        label: i18next.t('tanks:dataTable.columns.client'),
        type: DataType.TEXT
      },
      {
        name: 'destinations',
        label: i18next.t('tanks:dataTable.columns.destination'),
        type: DataType.MULTISELECT,
        selectOptions: destinations
      },
      {
        name: 'originPlant.description',
        label: i18next.t('tanks:dataTable.columns.originPlant'),
        type: DataType.TEXT,
        display: false // has been replaced by originPlant MULTISELECT but original value structure must remain in case needed in other view
      },
      {
        name: 'originPlantId',
        label: i18next.t('tanks:dataTable.columns.originPlant'),
        type: DataType.MULTISELECT,
        selectOptions: originPlants // replacement for originPlant.description to handle filterAdavancedSearch with MULTISELECT options.
      },
      {
        name: 'orderKg',
        label: i18next.t('tanks:dataTable.columns.orderKg'),
        type: DataType.NUMBER,
        decimalScale: DecimalScale.DEFAULT
      },
      {
        name: 'carriers',
        label: i18next.t('tanks:dataTable.columns.carrier'),
        type: DataType.MULTISELECT,
        selectOptions: carriers
      },
      {
        name: 'checkCompleted',
        label: i18next.t('tanks:dataTable.columns.completed'),
        type: DataType.BOOLEAN
      },
      {
        name: 'managedTripState',
        label: i18next.t('tanks:dataTable.columns.travelStatus'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(
          EnumEstadoViajeOpLogistica,
          'enums:EnumEstadoViajeOpLogistica'
        )
      },
      {
        name: 'relatedOperations',
        display: false
      }
    ]
  } else if (TPARole) {
    return [
      {
        name: 'actions',
        label: '',
        type: DataType.HTML,
        columnWidth: 25
      },
      {
        name: 'id',
        display: false
      },
      {
        name: 'orderDate',
        label: i18next.t('tanks:dataTable.columns.orderDate'),
        type: DataType.DATE
      },
      {
        name: 'tripDate',
        label: i18next.t('tanks:dataTable.columns.loadDate'),
        type: DataType.DATE
      },
      {
        name: 'dischargeDate',
        label: i18next.t('tanks:dataTable.columns.dischargeDate'),
        type: DataType.DATE
      },
      {
        name: 'tsoCode',
        label: i18next.t('tanks:dataTable.columns.orderNumber'),
        type: DataType.TEXT
      },
      {
        name: 'client.description',
        label: i18next.t('tanks:dataTable.columns.client'),
        type: DataType.TEXT
      },
      {
        name: 'destinations',
        label: i18next.t('tanks:dataTable.columns.destination'),
        type: DataType.MULTISELECT,
        selectOptions: destinations
      },
      {
        name: 'originPlant.description',
        label: i18next.t('tanks:dataTable.columns.originPlant'),
        type: DataType.TEXT,
        display: false
      },
      {
        name: 'originPlantId',
        label: i18next.t('tanks:dataTable.columns.originPlant'),
        type: DataType.MULTISELECT,
        selectOptions: originPlants // replacement for originPlant.description to handle filterAdavancedSearch with MULTISELECT options.
      },
      {
        name: 'orderQuantity',
        label: i18next.t('tanks:dataTable.columns.orderQuantity'),
        type: DataType.NUMBER,
        decimalScale: DecimalScale.DEFAULT
      },
      {
        name: 'orderKg',
        label: i18next.t('tanks:dataTable.columns.orderKg'),
        type: DataType.NUMBER,
        decimalScale: DecimalScale.DEFAULT
      },
      {
        name: 'pcs',
        label: i18next.t('tanks:dataTable.columns.pcs'),
        type: DataType.NUMBER,
        decimalScale: DecimalScale.DEFAULT
      },
      {
        name: 'carriers',
        label: i18next.t('tanks:dataTable.columns.carrier'),
        type: DataType.MULTISELECT,
        selectOptions: carriers
      },
      {
        name: 'deliveryNote',
        label: i18next.t('tanks:dataTable.columns.deliveryNote'),
        type: DataType.TEXT
      },
      {
        name: 'checkCompleted',
        label: i18next.t('tanks:dataTable.columns.completed'),
        type: DataType.BOOLEAN
      },
      {
        name: 'tripState',
        label: i18next.t('tanks:dataTable.columns.travelStatus'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(
          EnumEstadoViajeOpLogistica,
          'enums:EnumEstadoViajeOpLogistica'
        )
      },
      {
        name: 'relatedOperations',
        display: false
      }
    ]
  } else {
    return [
      {
        name: 'actions',
        label: '',
        type: DataType.HTML,
        columnWidth: 25
      },
      {
        name: 'id',
        display: false
      },
      {
        name: 'tripDate',
        label: i18next.t('tanks:dataTable.columns.loadDate'),
        type: DataType.DATE
      },
      {
        name: 'dischargeDate',
        label: i18next.t('tanks:dataTable.columns.dischargeDate'),
        type: DataType.DATE
      },
      {
        name: 'tsoCode',
        label: i18next.t('tanks:dataTable.columns.orderNumber'),
        type: DataType.TEXT
      },
      {
        name: 'client.description',
        label: i18next.t('tanks:dataTable.columns.client'),
        type: DataType.TEXT
      },
      {
        name: 'destinations',
        label: i18next.t('tanks:dataTable.columns.destination'),
        type: DataType.MULTISELECT,
        selectOptions: destinations
      },
      {
        name: 'originPlant.description',
        label: i18next.t('tanks:dataTable.columns.originPlant'),
        type: DataType.TEXT,
        display: false
      },
      {
        name: 'originPlantId',
        label: i18next.t('tanks:dataTable.columns.originPlant'),
        type: DataType.MULTISELECT,
        selectOptions: originPlants // replacement for originPlant.description to handle filterAdavancedSearch with MULTISELECT options.
      },
      {
        name: 'quantity',
        label: i18next.t('tanks:dataTable.columns.quantity'),
        type: DataType.NUMBER,
        decimalScale: DecimalScale.DEFAULT
      },
      {
        name: 'kg',
        label: i18next.t('tanks:dataTable.columns.kg'),
        type: DataType.NUMBER,
        decimalScale: DecimalScale.DEFAULT
      },
      {
        name: 'pcs',
        label: i18next.t('tanks:dataTable.columns.pcs'),
        type: DataType.NUMBER,
        decimalScale: DecimalScale.DEFAULT
      },
      {
        name: 'carriers',
        label: i18next.t('tanks:dataTable.columns.carrier'),
        type: DataType.MULTISELECT,
        selectOptions: carriers
      },
      {
        name: 'deliveryNote',
        label: i18next.t('tanks:dataTable.columns.deliveryNote'),
        type: DataType.TEXT
      },
      {
        name: 'tripState',
        label: i18next.t('tanks:dataTable.columns.travelStatus'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(
          EnumEstadoViajeOpLogistica,
          'enums:EnumEstadoViajeOpLogistica'
        )
      },
      {
        name: 'relatedOperations',
        display: false
      }
    ]
  }
}
